// Public: /vendor-invite/:token. A business invited from Admin > Marketing confirms its details
// and accepts (it becomes a vendor account and gets its user ID and a temporary password),
// or asks not to be contacted again.
import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  Box, Container, Paper, Typography, Stack, Chip, TextField, Button, Alert, Checkbox, FormControlLabel, Link, CircularProgress, IconButton,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { VENDOR_INVITE_QUERY, RESPOND_VENDOR_INVITE_MUTATION, SIGNUP_LEGAL_DOCUMENTS_QUERY } from '../graphql/queries';
import LegalFooter from '../components/LegalFooter';
import { LegalReadDialog } from '../components/LegalAcceptanceGate';

const BENEFITS = [
  'Families planning weddings near you find your services and packages',
  'Bookings, agreements, vouchers and photos in one place, with an account manager for each wedding',
  'Free to join. You set your own prices and cancellation terms',
];

export default function VendorInvitePage() {
  const { token } = useParams();
  const { data, loading, error } = useQuery(VENDOR_INVITE_QUERY, { variables: { token } });
  const [respond, { data: done, loading: saving, error: saveError }] = useMutation(RESPOND_VENDOR_INVITE_MUTATION);
  const [form, setForm] = useState({ email: '', phone: '', contactPerson: '', acceptLegal: false });
  // The documents are read to the end in a dialog before they can be accepted
  const [reading, setReading] = useState(false);
  const { data: legalData } = useQuery(SIGNUP_LEGAL_DOCUMENTS_QUERY, { variables: { roleName: 'vendor' } });
  const legalDocs = legalData?.signupLegalDocuments || [];
  const inv = data?.vendorInvite;
  useEffect(() => {
    if (inv) setForm(f => ({ ...f, email: f.email || inv.email || '', phone: f.phone || inv.phone || '', contactPerson: f.contactPerson || inv.contactPerson || '' }));
  }, [inv]);
  const result = done?.respondVendorInvite;
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const send = (accept) => respond({ variables: { token, input: accept ? { accept: true, ...form } : { accept: false } } });

  let body;
  if (loading) body = <Stack alignItems="center" py={6}><CircularProgress /></Stack>;
  else if (error) body = <Alert severity="error">{error.message}</Alert>;
  else if (!inv || inv.status === 'closed') body = <Alert severity="info">This invitation link is not valid.</Alert>;
  else if (result?.status === 'do_not_contact' || (!result && inv.status === 'do_not_contact')) {
    body = <Alert severity="info">Done. We will not contact {inv.name} again. If you change your mind, write to us through the contact details below.</Alert>;
  } else if (result?.status === 'converted' || inv.status === 'converted') {
    body = (
      <Alert severity="success">
        <Typography fontWeight={700} mb={1}>Welcome aboard! {inv.name} now has a vendor account.</Typography>
        {result?.email && <>User ID: <b>{result.email}</b><br /></>}
        {result?.tempPassword ? (
          <>
            Temporary password: <b style={{ fontFamily: 'monospace', fontSize: 16 }}>{result.tempPassword}</b>
            <IconButton size="small" onClick={() => { try { navigator.clipboard.writeText(result.tempPassword); } catch (e) { /* ignore */ } }}><ContentCopyIcon fontSize="inherit" /></IconButton>
            <br /><b>Save it now: it is not shown again.</b> After signing in, set your own password with "Forgot password".<br />
          </>
        ) : result ? <>We have emailed your temporary password.<br /></> : <>Sign in with the details we sent you, or use "Forgot password".<br /></>}
        <Button component={RouterLink} to="/login" variant="contained" size="small" sx={{ mt: 1.5 }}>Sign in</Button>
        <Typography variant="body2" mt={1.5}>Next: choose your services, add packages, prices, photos and cancellation terms. Families see your listing once it is approved.</Typography>
      </Alert>
    );
  } else {
    body = (
      <>
        <Typography variant="h5" fontWeight={700}>{inv.name}</Typography>
        <Typography variant="body2" color="text.secondary" mb={1}>{[inv.address, inv.city, inv.state].filter(Boolean).join(', ')}</Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mb={2}>
          {inv.services.map(s => <Chip key={s} size="small" color="primary" variant="outlined" label={s} />)}
        </Stack>
        <Typography mb={1}>{inv.brandName || 'We'} would like to list your business as a wedding vendor.</Typography>
        <Box component="ul" sx={{ pl: 2.5, mt: 0, mb: 2 }}>
          {BENEFITS.map(b => <Typography component="li" variant="body2" key={b} mb={0.5}>{b}</Typography>)}
        </Box>
        <Stack spacing={2}>
          <TextField label="Email address (your user ID) *" type="email" value={form.email} onChange={set('email')} fullWidth />
          <TextField label="Phone families can call *" value={form.phone} onChange={set('phone')} fullWidth />
          <TextField label="Contact person" value={form.contactPerson} onChange={set('contactPerson')} fullWidth />
          <FormControlLabel
            control={<Checkbox checked={form.acceptLegal} onChange={e => (e.target.checked && legalDocs.length ? setReading(true) : setForm(f => ({ ...f, acceptLegal: e.target.checked })))} />}
            label={<Typography variant="body2">I have read and accept the{' '}
              <Link component={RouterLink} to="/legal/vendor-terms" target="_blank">Vendor Agreement</Link>, the{' '}
              <Link component={RouterLink} to="/legal/terms" target="_blank">Terms and Conditions</Link> and the{' '}
              <Link component={RouterLink} to="/legal/privacy" target="_blank">Privacy Policy</Link>, and I am authorised to act for this business.</Typography>}
          />
          {reading && (
            <LegalReadDialog documents={legalDocs} onCancel={() => setReading(false)}
              onAccept={() => { setForm(f => ({ ...f, acceptLegal: true })); setReading(false); }} />
          )}
          {saveError && <Alert severity="error">{saveError.message}</Alert>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="contained" size="large" disabled={saving || !form.email || !form.acceptLegal} onClick={() => send(true)}>
              {saving ? 'Creating your account…' : 'Accept and create my vendor account'}
            </Button>
            <Button color="inherit" disabled={saving} onClick={() => send(false)}>Not interested. Do not contact me</Button>
          </Stack>
        </Stack>
      </>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fdf6ff', display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 5 }, flex: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" mb={2}>
          <StorefrontIcon color="primary" />
          <Typography variant="h6" fontWeight={700}>{inv?.brandName ? `Join ${inv.brandName} as a vendor` : 'Vendor invitation'}</Typography>
        </Stack>
        <Paper elevation={2} sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 3 }}>{body}</Paper>
      </Container>
      <LegalFooter />
    </Box>
  );
}
